/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator } from "@azure/core-paging";
import { SimplePollerLike, OperationState } from "@azure/core-lro";
import {
  DedicatedHost,
  DedicatedHostsListByHostGroupOptionalParams,
  DedicatedHostsListAvailableSizesOptionalParams,
  DedicatedHostsGetOptionalParams,
  DedicatedHostsGetResponse,
  DedicatedHostsCreateOrUpdateOptionalParams,
  DedicatedHostsCreateOrUpdateResponse,
  DedicatedHostUpdate,
  DedicatedHostsUpdateOptionalParams,
  DedicatedHostsUpdateResponse,
  DedicatedHostsDeleteOptionalParams,
  DedicatedHostsRedeployOptionalParams,
  DedicatedHostsRedeployResponse,
  DedicatedHostsRestartOptionalParams,
  DedicatedHostsRestartResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a DedicatedHosts. */
export interface DedicatedHosts {
  /**
   * Lists all of the dedicated hosts in the specified dedicated host group. Use the nextLink property in
   * the response to get the next page of dedicated hosts.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param options The options parameters.
   */
  listByHostGroup(
    resourceGroupName: string,
    hostGroupName: string,
    options?: DedicatedHostsListByHostGroupOptionalParams,
  ): PagedAsyncIterableIterator<DedicatedHost>;
  /**
   * Lists all available dedicated host sizes to which the specified dedicated host can be resized. NOTE:
   * The dedicated host sizes provided can be used to only scale up the existing dedicated host.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  listAvailableSizes(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsListAvailableSizesOptionalParams,
  ): PagedAsyncIterableIterator<string>;
  /**
   * Retrieves information about a dedicated host.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsGetOptionalParams,
  ): Promise<DedicatedHostsGetResponse>;
  /**
   * Create or update a dedicated host .
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param parameters Parameters supplied to the Create Dedicated Host.
   * @param options The options parameters.
   */
  beginCreateOrUpdate(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    parameters: DedicatedHost,
    options?: DedicatedHostsCreateOrUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<DedicatedHostsCreateOrUpdateResponse>,
      DedicatedHostsCreateOrUpdateResponse
    >
  >;
  /**
   * Create or update a dedicated host .
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param parameters Parameters supplied to the Create Dedicated Host.
   * @param options The options parameters.
   */
  beginCreateOrUpdateAndWait(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    parameters: DedicatedHost,
    options?: DedicatedHostsCreateOrUpdateOptionalParams,
  ): Promise<DedicatedHostsCreateOrUpdateResponse>;
  /**
   * Update a dedicated host .
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param parameters Parameters supplied to the Update Dedicated Host operation.
   * @param options The options parameters.
   */
  beginUpdate(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    parameters: DedicatedHostUpdate,
    options?: DedicatedHostsUpdateOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<DedicatedHostsUpdateResponse>,
      DedicatedHostsUpdateResponse
    >
  >;
  /**
   * Update a dedicated host .
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param parameters Parameters supplied to the Update Dedicated Host operation.
   * @param options The options parameters.
   */
  beginUpdateAndWait(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    parameters: DedicatedHostUpdate,
    options?: DedicatedHostsUpdateOptionalParams,
  ): Promise<DedicatedHostsUpdateResponse>;
  /**
   * Delete a dedicated host.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsDeleteOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>>;
  /**
   * Delete a dedicated host.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsDeleteOptionalParams,
  ): Promise<void>;
  /**
   * Redeploy the dedicated host. The operation will complete successfully once the dedicated host has
   * migrated to a new node and is running. To determine the health of VMs deployed on the dedicated host
   * after the redeploy check the Resource Health Center in the Azure Portal. Please refer to
   * https://docs.microsoft.com/azure/service-health/resource-health-overview for more details.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  beginRedeploy(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsRedeployOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<DedicatedHostsRedeployResponse>,
      DedicatedHostsRedeployResponse
    >
  >;
  /**
   * Redeploy the dedicated host. The operation will complete successfully once the dedicated host has
   * migrated to a new node and is running. To determine the health of VMs deployed on the dedicated host
   * after the redeploy check the Resource Health Center in the Azure Portal. Please refer to
   * https://docs.microsoft.com/azure/service-health/resource-health-overview for more details.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  beginRedeployAndWait(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsRedeployOptionalParams,
  ): Promise<DedicatedHostsRedeployResponse>;
  /**
   * Restart the dedicated host. The operation will complete successfully once the dedicated host has
   * restarted and is running. To determine the health of VMs deployed on the dedicated host after the
   * restart check the Resource Health Center in the Azure Portal. Please refer to
   * https://docs.microsoft.com/azure/service-health/resource-health-overview for more details.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  beginRestart(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsRestartOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<DedicatedHostsRestartResponse>,
      DedicatedHostsRestartResponse
    >
  >;
  /**
   * Restart the dedicated host. The operation will complete successfully once the dedicated host has
   * restarted and is running. To determine the health of VMs deployed on the dedicated host after the
   * restart check the Resource Health Center in the Azure Portal. Please refer to
   * https://docs.microsoft.com/azure/service-health/resource-health-overview for more details.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param hostGroupName The name of the dedicated host group.
   * @param hostName The name of the dedicated host.
   * @param options The options parameters.
   */
  beginRestartAndWait(
    resourceGroupName: string,
    hostGroupName: string,
    hostName: string,
    options?: DedicatedHostsRestartOptionalParams,
  ): Promise<DedicatedHostsRestartResponse>;
}
